import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaChartLine, FaDiscord, FaGithub, FaHistory, FaUsers } from "react-icons/fa";

const LAST_SCOUT_STORAGE_KEY = "myimpactgame:last-scout";

const C = {
  bg: "#08090d",
  surface: "#12141b",
  panel: "#0b1020",
  border: "#272b36",
  muted: "#a7adbb",
  quiet: "#707789",
  text: "#f6f7fb",
  blue: "#3b82f6",
  blue2: "#60a5fa",
  green: "#4ade80",
  red: "#fb7185",
};

const cards = [
  {
    title: "Scout teams",
    text: "Compare two teams from OP.GG multisearch links.",
    icon: <FaUsers />,
  },
  {
    title: "Read players",
    text: "See recent champions, winrates, mastery, ranks and match history.",
    icon: <FaHistory />,
  },
  {
    title: "Understand impact",
    text: "Use Recent Impact to see player tendencies beyond basic stats.",
    icon: <FaChartLine />,
  },
];

function Header() {
  return (
    <header style={{ borderBottom: `1px solid ${C.border}`, background: "rgba(8,9,13,0.88)", backdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 5 }}>
      <div style={{ width: "min(1120px, calc(100% - 32px))", margin: "0 auto", height: 66, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, color: C.text, textDecoration: "none" }}>
          <span style={{ width: 36, height: 36, display: "grid", placeItems: "center", borderRadius: 10, background: C.blue, color: "white", fontWeight: 950 }}>IG</span>
          <span style={{ fontWeight: 850 }}>MyImpactGame</span>
        </Link>
        <nav style={{ display: "flex", alignItems: "center", gap: 18, fontSize: 14 }}>
          <Link to="/about" style={{ color: C.muted, textDecoration: "none" }}>About</Link>
          <Link to="/scout" style={{ color: C.text, textDecoration: "none", border: `1px solid ${C.border}`, borderRadius: 10, padding: "9px 12px", background: C.surface, fontWeight: 800 }}>Start Scouting</Link>
        </nav>
      </div>
    </header>
  );
}

function TeamCard({ side = "green", role }) {
  const color = side === "green" ? C.green : C.red;
  return (
    <div style={{ border: `1px solid ${side === "green" ? "rgba(74,222,128,0.32)" : "rgba(251,113,133,0.32)"}`, borderTop: `2px solid ${color}`, borderRadius: 10, background: side === "green" ? "#07130e" : "#170b10", padding: 10, minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", color, fontSize: 10, fontWeight: 900 }}>
        <span>{role}</span>
        <span>D2</span>
      </div>
      <div style={{ height: 9, width: "80%", borderRadius: 999, background: "#2f3542", marginTop: 10 }} />
      <div style={{ display: "flex", gap: 5, marginTop: 10 }}>
        <span style={{ width: 22, height: 22, borderRadius: 7, background: color }} />
        <span style={{ width: 22, height: 22, borderRadius: 7, background: "#2b2f3a" }} />
        <span style={{ width: 22, height: 22, borderRadius: 7, background: "#2b2f3a" }} />
      </div>
    </div>
  );
}

function Bar({ label, value, color }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "72px 1fr", gap: 8, alignItems: "center", marginBottom: 9 }}>
      <span style={{ color: C.muted, fontSize: 10 }}>{label}</span>
      <span style={{ height: 8, borderRadius: 999, background: "#2b303a", overflow: "hidden" }}>
        <i style={{ display: "block", width: `${value}%`, height: "100%", borderRadius: 999, background: color }} />
      </span>
    </div>
  );
}

function ProductMockup() {
  const roles = ["TOP", "JGL", "MID", "BOT", "SUP"];
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 18, background: `linear-gradient(180deg, ${C.surface}, #0b1020)`, padding: 16, boxShadow: "0 24px 90px rgba(0,0,0,0.38)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <strong style={{ display: "block", color: C.text, fontSize: 14 }}>Team scouting page</strong>
          <span style={{ color: C.quiet, fontSize: 12 }}>10 players ready for review</span>
        </div>
        <span style={{ border: `1px solid ${C.border}`, borderRadius: 999, padding: "6px 9px", color: C.green, fontSize: 11, fontWeight: 850 }}>Impact view</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 8, marginBottom: 14 }}>
        {roles.map((role) => <TeamCard key={`green-${role}`} role={role} />)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, background: C.panel, padding: 12 }}>
          <strong style={{ display: "block", color: C.green, fontSize: 12, marginBottom: 12 }}>Team one</strong>
          <Bar label="Damage" value={74} color={C.green} />
          <Bar label="Vision" value={58} color={C.green} />
          <Bar label="Control" value={42} color={C.green} />
        </div>
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, background: C.panel, padding: 12 }}>
          <strong style={{ display: "block", color: C.blue2, fontSize: 12, marginBottom: 12 }}>Difference</strong>
          <Bar label="Damage" value={55} color={C.blue2} />
          <Bar label="Vision" value={38} color={C.blue2} />
          <Bar label="Control" value={62} color={C.blue2} />
        </div>
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, background: C.panel, padding: 12 }}>
          <strong style={{ display: "block", color: C.red, fontSize: 12, marginBottom: 12 }}>Team two</strong>
          <Bar label="Damage" value={63} color={C.red} />
          <Bar label="Vision" value={51} color={C.red} />
          <Bar label="Control" value={36} color={C.red} />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 8 }}>
        {roles.map((role) => <TeamCard key={`red-${role}`} side="red" role={role} />)}
      </div>
    </div>
  );
}

function FeatureCard({ title, text, icon }) {
  return (
    <article style={{ border: `1px solid ${C.border}`, borderRadius: 16, background: C.surface, padding: 20 }}>
      <span style={{ width: 38, height: 38, borderRadius: 12, background: "rgba(59,130,246,0.12)", color: C.blue2, display: "grid", placeItems: "center", marginBottom: 16 }}>{icon}</span>
      <h3 style={{ margin: 0, color: C.text, fontSize: 19 }}>{title}</h3>
      <p style={{ margin: "10px 0 0", color: C.muted, lineHeight: 1.65, fontSize: 14 }}>{text}</p>
    </article>
  );
}

export default function HomePage() {
  const [hasLastScout, setHasLastScout] = useState(false);

  useEffect(() => {
    try {
      setHasLastScout(Boolean(window.sessionStorage.getItem(LAST_SCOUT_STORAGE_KEY)));
    } catch (_) {
      setHasLastScout(false);
    }
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text }}>
      <Header />
      <main>
        <section style={{ width: "min(1120px, calc(100% - 32px))", margin: "0 auto", padding: "56px 0 46px" }}>
          <div style={{ maxWidth: 880 }}>
            <p style={{ margin: "0 0 10px", color: C.blue2, fontSize: 12, fontWeight: 950, textTransform: "uppercase", letterSpacing: "0.08em" }}>League of Legends team scouting</p>
            <h1 style={{ margin: 0, maxWidth: 860, color: C.text, fontSize: "clamp(38px, 5vw, 58px)", lineHeight: 1.04, fontWeight: 950 }}>
              MyImpactGame helps amateur LoL teams understand players, prepare matches and make better draft decisions.
            </h1>
            <p style={{ margin: "22px 0 0", maxWidth: 650, color: C.muted, fontSize: 18, lineHeight: 1.72 }}>
              Paste two OP.GG multisearch links and get a clear overview of both teams: player profiles, champion pools, recent games, mastery, ranks and recent impact.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 26 }}>
              <Link to="/scout" style={{ display: "inline-flex", alignItems: "center", gap: 9, minHeight: 48, borderRadius: 10, background: C.blue, color: "white", padding: "0 18px", fontWeight: 900, textDecoration: "none" }}>
                Start Scouting <FaArrowRight />
              </Link>
              {hasLastScout && (
                <Link to="/scout" state={{ restoreLastScout: true }} style={{ display: "inline-flex", alignItems: "center", gap: 9, minHeight: 48, borderRadius: 10, background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.36)", color: C.green, padding: "0 18px", fontWeight: 900, textDecoration: "none" }}>
                  Return to last analysis
                </Link>
              )}
              <Link to="/about" style={{ display: "inline-flex", alignItems: "center", minHeight: 48, borderRadius: 10, border: `1px solid ${C.border}`, color: C.text, padding: "0 18px", fontWeight: 850, textDecoration: "none", background: C.surface }}>
                Learn about Recent Impact
              </Link>
            </div>
          </div>
          <div style={{ maxWidth: 760, marginTop: 36 }}>
            <ProductMockup />
          </div>
        </section>

        <section style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, background: "#0b0d13" }}>
          <div style={{ width: "min(1120px, calc(100% - 32px))", margin: "0 auto", padding: "44px 0", display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 14 }}>
            {cards.map((card) => (
              <FeatureCard key={card.title} title={card.title} text={card.text} icon={card.icon} />
            ))}
          </div>
        </section>

        <section style={{ width: "min(1120px, calc(100% - 32px))", margin: "0 auto", padding: "42px 0 58px" }}>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 18, background: C.surface, padding: 22, display: "grid", gridTemplateColumns: "1fr auto", gap: 18, alignItems: "center" }}>
            <div>
              <p style={{ margin: "0 0 8px", color: C.blue2, fontSize: 12, fontWeight: 950, textTransform: "uppercase", letterSpacing: "0.08em" }}>Current MVP</p>
              <p style={{ margin: 0, color: C.muted, lineHeight: 1.7 }}>
                Current MVP: team scouting page, recent player data, impact vectors and champion-specific impact view.
              </p>
            </div>
            <Link to="/scout" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: 44, borderRadius: 10, background: C.blue, color: "white", padding: "0 16px", fontWeight: 900, textDecoration: "none", whiteSpace: "nowrap" }}>
              Start Scouting
            </Link>
          </div>
        </section>
      </main>

      <footer style={{ borderTop: `1px solid ${C.border}`, background: "rgba(8,9,13,0.92)" }}>
        <div style={{ width: "min(1120px, calc(100% - 32px))", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "18px 0", color: C.muted, fontSize: 12 }}>
          <span>(c) {new Date().getFullYear()} MyImpactGame - Not affiliated with Riot Games.</span>
          <div style={{ display: "flex", gap: 14, fontSize: 18 }}>
            <a href="https://github.com/Rambele/MyLoLStyle" target="_blank" rel="noreferrer" style={{ color: C.muted }}><FaGithub /></a>
            <span onClick={() => window.open("https://discord.com/users/rambel1", "_blank")} style={{ cursor: "pointer", color: C.muted }}><FaDiscord /></span>
          </div>
        </div>
      </footer>

      <style>{`
        @media (max-width: 1040px) {
          main section,
          main section > div {
            grid-template-columns: 1fr !important;
          }
        }

        @media (max-width: 740px) {
          header nav {
            display: none !important;
          }

          main section:first-child {
            padding-top: 48px !important;
          }
        }
      `}</style>
    </div>
  );
}
